import { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  IconButton,
  Divider,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Menu,
  MenuItem,
  ListItemText,
  useTheme,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PrintIcon from "@mui/icons-material/Print";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import dayjs from "dayjs";
import { toast } from "react-toastify";

import { useAuth } from "../../context/AuthContext.jsx";
import {
  fetchOrganizationProfile,
  getOrganizationId,
  getOrganizationName,
} from "../../utils/organizationUtils.js";
import { getPatientById } from "../../services/patientService.js";
import { displayId } from "../../utils/patientTimelineVisitUtils.js";
import AddNewLabTest from "./AddNewLabTest.jsx";
import EditLabTest from "./EditLabTest.jsx";
import {
  enrichReportsWithDoctorNames,
  formatLabReportReferenceDisplay,
  formatReportDateOnly,
  getResultDisplayRows,
  printPathologyLabReport,
  resolveReferringDoctorName,
} from "./labReportDisplayUtils.js";
import {
  getPathLabReportResultValue,
  resolvePathLabReportDate,
  resolvePathLabReportDateKey,
} from "../../utils/labTestDedupeUtils.js";
import {
  getLabTestCatalogMaps,
  getPathLabReportsForPatient,
  resolveCatalogTestForReport,
  resolveLabTestReferenceMeta,
} from "../PathManagePatient/labTestWorkflowUtils.js";
import {
  notifyPathLabDataUpdated,
  PATH_LAB_DATA_UPDATED_EVENT,
} from "../../utils/opdPatientsRefresh.js";
import {
  printPathologyLabInvoiceForDayReports,
  groupReportsByPrescriptionScope,
} from "../../utils/invoiceReprintUtils.js";
import { invalidateOrgPathLabReportsLiteCache } from "../../services/pathLabTestReportService.js";

const isLabReportBilled = (report) => {
  const rs = String(
    report?.resultStatus ?? report?.result_status ?? report?.status ?? ""
  ).toUpperCase();
  if (["BILLED", "COLLECTED", "COMPLETE", "COMPLETED"].includes(rs)) {
    return true;
  }
  const result = report?.result ?? report?.testResult;
  if (result == null || result === "") return false;
  if (typeof result === "string") return result.trim().length > 0;
  if (typeof result === "object") return Object.keys(result).length > 0;
  return Boolean(result);
};

const hasPrintableLabResult = (report) => {
  if (!report) return false;
  const result = report.result ?? report.testResult;
  if (result == null) return false;
  if (typeof result === "string") return result.trim().length > 0;
  if (typeof result === "object") return Object.keys(result).length > 0;
  return true;
};

// Builds one display line per sub-category (or a single line for a simple
// test) for the Result / Ref. Value columns, so a multi-parameter test (e.g.
// a urine panel) reads as a stacked list instead of one run-on line.
const buildResultReferenceLines = (report) => {
  const display = getResultDisplayRows(report.testName, report.result, {
    subCategories: report.subCategories,
    normalRange: report.normalRange,
    referenceValue: report.referenceValue,
    unit: report.unit,
  });

  if (display.type === "table") {
    return {
      resultLines: display.rows.map((row) => `${row.name}: ${row.result || "—"}`),
      referenceLines: display.rows.map(
        (row) => formatLabReportReferenceDisplay(row.referenceValue, row.unit) || "—"
      ),
      remarks: display.remarks,
    };
  }

  return {
    resultLines: [display.simpleResult || "—"],
    referenceLines: [display.referenceDisplay || "—"],
    remarks: display.remarks,
  };
};

// Single "Print" menu per visit — lists the visit invoice (once billed) and
// every test that already has a result, instead of a separate print button
// per report card. Mirrors PatientTimelinePrintMenu's pattern in the OPD/IPD
// patient timeline.
const VisitPrintMenu = ({
  groupReports,
  canPrintInvoice,
  isPrintingInvoice,
  onPrintInvoice,
  patient,
  organizationName,
  hospitalAddress,
  hospitalPhone,
  doctorName,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const printableReports = (groupReports || []).filter(hasPrintableLabResult);

  const handlePrintReport = (report) => {
    setAnchorEl(null);
    printPathologyLabReport({
      report,
      patient,
      organizationName,
      hospitalAddress,
      hospitalPhone,
      referringDoctorName: resolveReferringDoctorName(report, doctorName),
    });
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={isPrintingInvoice ? <CircularProgress size={14} /> : <PrintIcon />}
        endIcon={<ArrowDropDownIcon />}
        disabled={isPrintingInvoice}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        Print
      </Button>
      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            onPrintInvoice();
          }}
          disabled={!canPrintInvoice || isPrintingInvoice}
        >
          <ListItemText
            primary="Print Invoice"
            secondary={canPrintInvoice ? undefined : "Available after billing"}
          />
        </MenuItem>
        <Divider />
        {printableReports.length === 0 ? (
          <MenuItem disabled>
            <ListItemText primary="No completed reports to print yet" />
          </MenuItem>
        ) : (
          printableReports.map((report, idx) => (
            <MenuItem
              key={report.reportId || `${report.testName}-${idx}`}
              onClick={() => handlePrintReport(report)}
            >
              <ListItemText primary={`${report.testName} Report`} />
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
};

export default function PathLabTestTable({ appointment, patient: initialPatient = null }) {
  const { user } = useAuth();
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main || "#1976d2";
  const fetchGenRef = useRef(0);

  const [pathLabTests, setPathLabTests] = useState({});
  const [fetching, setFetching] = useState(false);
  const [openLabModal, setOpenLabModal] = useState({ open: false, reports: {} });
  const [editLabTest, setEditLabTest] = useState(false);
  const [labTestInfo, setLabTestInfo] = useState(null);
  // Caller (the prescription modal) may already have fetched this patient —
  // seed from it so we skip our own GET /patients/{id} when it matches.
  const [patientDetails, setPatientDetails] = useState(initialPatient);
  const [orgProfile, setOrgProfile] = useState({ address: "", phone: "" });
  const [printingInvoiceKey, setPrintingInvoiceKey] = useState(null);

  const organizationName = getOrganizationName(user) || "Hospital";

  const applyReportsToState = useCallback(
    async (rawData, generation) => {
      if (generation !== fetchGenRef.current) return;

      let data = Array.isArray(rawData) ? rawData : [];
      const appointmentPatientId = appointment?.patientId;
      data = data
        .filter((row) => {
          if (appointmentPatientId != null) {
            const rowPatientId = row?.patientId ?? row?.patient_id;
            if (
              rowPatientId != null &&
              Number(rowPatientId) !== Number(appointmentPatientId)
            ) {
              return false;
            }
          }
          const status = String(
            row?.resultStatus ?? row?.result_status ?? row?.status ?? ""
          ).toUpperCase();
          return status !== "REJECT" && status !== "REJECTED";
        })
        .sort((a, b) => Number(b.reportId ?? b.id ?? 0) - Number(a.reportId ?? a.id ?? 0));

      const grouped = data.reduce((acc, curr) => {
        const dateKey = resolvePathLabReportDateKey(curr) || "undated";
        if (!acc[dateKey]) acc[dateKey] = [];

        const testName =
          curr.testName || curr.test_name || curr.name || curr.labTestName || "";
        if (!String(testName).trim()) return acc;

        acc[dateKey].push({
          reportId: curr.reportId || curr.id,
          testId: curr.testId || curr.labTestId,
          labTestId: curr.labTestId || curr.testId,
          testName: testName || "—",
          result: getPathLabReportResultValue(curr),
          reportDate: resolvePathLabReportDate(curr) || curr.reportDate,
          doctorId: curr.doctorId,
          doctorName: curr.doctorName,
          assignedByName: curr.assignedByName,
          patientId: curr.patientId || appointment?.patientId,
          patientName: curr.patientName || appointment?.patientName,
          prescriptionId: curr.prescriptionId ?? curr.prescription_id ?? null,
          appointmentId:
            curr.appointmentId ??
            curr.appointment_id ??
            appointment?.id ??
            appointment?.appointmentId ??
            null,
          normalRange: curr.normalRange || curr.referenceValue,
          referenceValue: curr.referenceValue || curr.normalRange,
          unit: curr.unit || "",
          // Catalog/custom sub-categories carry org-specific reference values
          subCategories: Array.isArray(curr.subCategories) ? curr.subCategories : [],
          resultStatus: curr.resultStatus ?? curr.result_status ?? curr.status,
          status: curr.status ?? curr.resultStatus ?? curr.result_status,
        });

        return acc;
      }, {});

      const sortedDateKeys = Object.keys(grouped).sort((a, b) => {
        if (a === "undated") return 1;
        if (b === "undated") return -1;
        return dayjs(b).valueOf() - dayjs(a).valueOf();
      });

      const allReports = sortedDateKeys.flatMap((dateKey) => grouped[dateKey]);

      // Ensure Ref. Value is filled for standalone/custom tests (catalog + predefined params)
      let reportsWithRefs = allReports;
      try {
        const { catalogById, catalogByName } = await getLabTestCatalogMaps();
        reportsWithRefs = allReports.map((report) => {
          const catalogTest = resolveCatalogTestForReport(
            report,
            catalogById,
            catalogByName
          );
          const refMeta = resolveLabTestReferenceMeta({
            testName: report.testName,
            catalogTest,
            report,
            subCategories: report.subCategories,
          });
          if (!refMeta.referenceValue && !refMeta.unit) return report;
          return {
            ...report,
            referenceValue: report.referenceValue || refMeta.referenceValue || null,
            normalRange: report.normalRange || refMeta.referenceValue || null,
            unit: report.unit || refMeta.unit || "",
          };
        });
      } catch (err) {
        console.warn("[PathLabTest] Could not enrich reference values:", err);
      }

      const enrichedAll = await enrichReportsWithDoctorNames(
        reportsWithRefs,
        appointment?.patientId,
        appointment
      );
      if (generation !== fetchGenRef.current) return;

      const enrichedGrouped = {};
      let offset = 0;
      sortedDateKeys.forEach((dateKey) => {
        const count = grouped[dateKey].length;
        enrichedGrouped[dateKey] = enrichedAll.slice(offset, offset + count);
        offset += count;
      });

      setPathLabTests(enrichedGrouped);
    },
    [appointment]
  );

  const fetchPathLabTestList = useCallback(async () => {
    if (!appointment?.patientId) return;

    const generation = ++fetchGenRef.current;
    setFetching(true);
    try {
      // Ensure post-save refreshes do not reuse a stale org report cache.
      invalidateOrgPathLabReportsLiteCache();

      const fastData = await getPathLabReportsForPatient(appointment.patientId, {
        mergeOrgLite: false,
      });
      await applyReportsToState(fastData, generation);

      getPathLabReportsForPatient(appointment.patientId, { mergeOrgLite: true })
        .then((mergedData) => applyReportsToState(mergedData, generation))
        .catch((err) => {
          console.warn("[PathLabTest] Background org-lite merge failed:", err);
        });
    } catch (err) {
      console.error("Failed to fetch pathology reports:", err);
    } finally {
      if (generation === fetchGenRef.current) {
        setFetching(false);
      }
    }
  }, [appointment?.patientId, applyReportsToState]);

  useEffect(() => {
    if (appointment?.patientId) {
      fetchPathLabTestList();
    }
  }, [appointment?.patientId, fetchPathLabTestList]);

  useEffect(() => {
    let refreshTimer = null;
    let lastFetchAt = 0;

    const scheduleFetch = () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => {
        if (!appointment?.patientId) return;
        if (Date.now() - lastFetchAt < 2000) return;
        lastFetchAt = Date.now();
        fetchPathLabTestList();
      }, 400);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && appointment?.patientId) {
        // Skip rapid re-fetch when switching browser tabs during print.
        if (Date.now() - lastFetchAt < 15000) return;
        scheduleFetch();
      }
    };

    const handlePathLabDataUpdated = () => {
      if (appointment?.patientId) {
        scheduleFetch();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener(PATH_LAB_DATA_UPDATED_EVENT, handlePathLabDataUpdated);

    return () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener(PATH_LAB_DATA_UPDATED_EVENT, handlePathLabDataUpdated);
    };
  }, [appointment?.patientId, fetchPathLabTestList]);

  useEffect(() => {
    const loadPatientAndOrg = async () => {
      const seedId = initialPatient?.id ?? initialPatient?.patientId ?? initialPatient?.patient_id;
      const seedMatches =
        initialPatient != null &&
        seedId != null &&
        appointment?.patientId != null &&
        String(seedId) === String(appointment.patientId);

      if (appointment?.patientId && !seedMatches) {
        try {
          const patient = await getPatientById(appointment.patientId);
          setPatientDetails(patient);
        } catch (_) {
          setPatientDetails(null);
        }
      }

      const organizationId = getOrganizationId(user);
      if (organizationId) {
        try {
          const profile = await fetchOrganizationProfile(organizationId);
          setOrgProfile({
            address: profile?.address || "",
            phone: profile?.phone || "",
          });
        } catch (_) {
          setOrgProfile({ address: "", phone: "" });
        }
      }
    };

    loadPatientAndOrg();
  }, [appointment?.patientId, user, initialPatient]);

  const handleLabDataRefresh = async () => {
    await fetchPathLabTestList();
    notifyPathLabDataUpdated();
  };

  const handlePrintVisitInvoice = async (dateKey, reports, groupKey) => {
    if (!reports?.length || printingInvoiceKey != null) return;
    if (!(reports || []).some(isLabReportBilled)) return;

    setPrintingInvoiceKey(groupKey);
    try {
      const first = reports[0] || {};
      await printPathologyLabInvoiceForDayReports(reports, {
        user,
        patient: patientDetails,
        dateKey,
        prescriptionId: first.prescriptionId ?? first.prescription_id ?? null,
        appointmentId:
          first.appointmentId ??
          first.appointment_id ??
          appointment?.id ??
          appointment?.appointmentId ??
          null,
        patientsLookup: patientDetails?.id
          ? {
              [patientDetails.id]: patientDetails,
              [String(patientDetails.id)]: patientDetails,
            }
          : {},
      });
    } catch (error) {
      console.error("[PathLabTest] Failed to print visit pathology invoice:", error);
      toast.error(error?.message || "Failed to print pathology invoice for this visit");
    } finally {
      setPrintingInvoiceKey(null);
    }
  };

  // Flatten date -> visit-groups into one list so each visit gets its own
  // timeline entry (dot + card), matching the Patient History tab exactly
  // instead of nesting multiple visits inside one shared per-day card (which
  // was showing the date twice — once outside the card, once inside it).
  const visitEntries = Object.entries(pathLabTests).flatMap(([dateKey, reports]) =>
    groupReportsByPrescriptionScope(reports).map((groupReports, groupIdx) => ({
      dateKey,
      groupReports,
      groupIdx,
    }))
  );

  return (
    <Box sx={{ p: 2, backgroundColor: "#fff" }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 1 }}>
        <Box display="flex" gap={1}>
          {user?.roleName === "frontdesk" && (
            <Button
              size="small"
              variant="contained"
              onClick={() => setOpenLabModal({ open: true, reports: pathLabTests })}
            >
              Add Result
            </Button>
          )}
          <IconButton size="small" onClick={fetchPathLabTestList}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {fetching && visitEntries.length === 0 ? (
        <CircularProgress />
      ) : visitEntries.length === 0 ? (
        <Typography>No Previous Data</Typography>
      ) : (
        <Box sx={{ position: "relative", ml: 6 }}>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: -49,
              bottom: 0,
              width: "2px",
              backgroundColor: primaryColor,
            }}
          />

          {visitEntries.map(({ dateKey, groupReports, groupIdx }, idx) => {
            const dateLabel =
              dateKey === "undated"
                ? "Undated"
                : dayjs(dateKey).isValid()
                  ? dayjs(dateKey).format("DD MMM YYYY")
                  : dateKey;
            const groupKey = `${dateKey}-${groupIdx}`;
            const groupDoctor =
              groupReports.find((r) => r.doctorName)?.doctorName ||
              appointment?.doctorName ||
              "";
            const first = groupReports[0] || {};
            const groupPrescriptionId =
              first.prescriptionId ?? first.prescription_id ?? null;
            const groupAppointmentId =
              first.appointmentId ??
              first.appointment_id ??
              appointment?.id ??
              appointment?.appointmentId ??
              null;
            const canPrintInvoice = (groupReports || []).some(isLabReportBilled);

            return (
              <Box key={idx} sx={{ display: "flex", mb: 2.5, position: "relative" }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: primaryColor,
                    position: "absolute",
                    left: "-54px",
                    top: "16px",
                  }}
                />

                <Box sx={{ flex: 2 }}>
                  <Paper elevation={2} sx={{ p: 2 }}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      gap={2}
                      flexWrap="wrap"
                      sx={{ mb: 1 }}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight="bold" sx={{ color: primaryColor }}>
                          {dateLabel}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          Appointment ID: {displayId(groupAppointmentId)}
                          {"  |  "}
                          Prescription ID: {displayId(groupPrescriptionId)}
                        </Typography>
                      </Box>
                      <VisitPrintMenu
                        groupReports={groupReports}
                        canPrintInvoice={canPrintInvoice}
                        isPrintingInvoice={printingInvoiceKey === groupKey}
                        onPrintInvoice={() =>
                          handlePrintVisitInvoice(dateKey, groupReports, groupKey)
                        }
                        patient={patientDetails}
                        organizationName={organizationName}
                        hospitalAddress={orgProfile.address}
                        hospitalPhone={orgProfile.phone}
                        doctorName={groupDoctor}
                      />
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: primaryColor }}>
                          <TableCell sx={{ color: "#fff" }}>Report ID</TableCell>
                          <TableCell sx={{ color: "#fff" }}>Test Name</TableCell>
                          <TableCell sx={{ color: "#fff" }}>Result</TableCell>
                          <TableCell sx={{ color: "#fff" }}>Ref. Value</TableCell>
                          <TableCell sx={{ color: "#fff" }}>Report Date</TableCell>
                          <TableCell sx={{ color: "#fff" }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {groupReports.map((report, reportIdx) => {
                          const { resultLines, referenceLines, remarks } =
                            buildResultReferenceLines(report);
                          return (
                            <TableRow
                              key={
                                report.reportId ||
                                `${report.testName}-${report.reportDate}-${reportIdx}`
                              }
                            >
                              <TableCell>{displayId(report.reportId)}</TableCell>
                              <TableCell>{report.testName}</TableCell>
                              <TableCell>
                                {resultLines.map((line, i) => (
                                  <div key={i}>{line}</div>
                                ))}
                                {remarks ? (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    component="div"
                                  >
                                    Remarks: {remarks}
                                  </Typography>
                                ) : null}
                              </TableCell>
                              <TableCell>
                                {referenceLines.map((line, i) => (
                                  <div key={i}>{line}</div>
                                ))}
                              </TableCell>
                              <TableCell>{formatReportDateOnly(report.reportDate)}</TableCell>
                              <TableCell>{report.resultStatus || "—"}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Paper>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      <AddNewLabTest
        open={openLabModal.open}
        onClose={async () => {
          setOpenLabModal({ open: false, reports: {} });
          await handleLabDataRefresh();
        }}
        appointment={appointment}
        existingReports={openLabModal.reports}
      />

      <EditLabTest
        open={editLabTest}
        onClose={async () => {
          setEditLabTest(false);
          setLabTestInfo(null);
          await handleLabDataRefresh();
        }}
        lab_report={labTestInfo}
      />
    </Box>
  );
}
